import React, { useCallback } from 'react';

import CloseIcon from '@publicImages/svg/quick-find-coaching-close.svg?static';

import type { FiltersKey } from '@/ducks/filters/types';

import { resetFilters } from '@/ducks/filters/setters';
import { getSearchParamsValue } from '@/ducks/filters/utils/primitive';
import tracking from '@/ducks/pages/chooseVoyage/tracking';
import { getQFSearchParams } from '@/features/quickFindFilters/helpers';
import { useQFActiveLink } from '@/features/quickFindFilters/hooks';
import getCurrentSearchParams from '@/helpers/url/getCurrentSearchParams';
import useOnKeyDown from '@/hooks/useOnKeyDown';

const SelectedFilterChip = ({
  filterKeys,
  label,
  onBeforeRemoveFilter,
  onOpenFilterModal,
}: {
  filterKeys: FiltersKey | FiltersKey[];
  label: string;
  onBeforeRemoveFilter?: CallableFunction;
  onOpenFilterModal: CallableFunction;
}) => {
  const openFilterSelectionSection = useCallback(
    (filterKeys: FiltersKey | FiltersKey[]) => () => {
      onOpenFilterModal(filterKeys);
    },
    [onOpenFilterModal],
  );

  const removeSelectedFilter = useCallback(
    (filterKeys: FiltersKey | FiltersKey[]) => () => {
      if (typeof onBeforeRemoveFilter === 'function') {
        onBeforeRemoveFilter();
      }

      resetFilters(filterKeys);
      tracking.trackDefaultFilter(filterKeys);
    },
    [onBeforeRemoveFilter],
  );

  const onKeydownRemove = useOnKeyDown(removeSelectedFilter(filterKeys));
  const onKeyDownOpenFilter = useOnKeyDown(openFilterSelectionSection(filterKeys));

  const searchParams = getCurrentSearchParams();
  const quickFindID = searchParams.get('quickFindID');
  const activeQFLink = useQFActiveLink();
  const linkParams = getQFSearchParams(activeQFLink);

  const isInSearchParams = (Array.isArray(filterKeys) ? filterKeys : [filterKeys]).some((key) =>
    getSearchParamsValue(key, searchParams),
  );
  const isInActiveQFLink = (Array.isArray(filterKeys) ? filterKeys : [filterKeys]).some((key) =>
    getSearchParamsValue(key, linkParams),
  );

  if (!isInSearchParams || (quickFindID && isInActiveQFLink)) {
    return null;
  }

  return (
    <div className="filter" key={filterKeys.toString()}>
      <div
        className="text"
        onClick={openFilterSelectionSection(filterKeys)}
        onKeyDown={onKeyDownOpenFilter}
        role="button"
        tabIndex={0}
      >
        {label}
      </div>
      <div
        aria-label="Remove the filter option"
        className="removeChipAction"
        onClick={removeSelectedFilter(filterKeys)}
        onKeyDown={onKeydownRemove}
        role="button"
        tabIndex={0}
      >
        <CloseIcon className="removeChipAction__icon" />
      </div>
    </div>
  );
};

export default SelectedFilterChip;
